import { useToast as useNotificationsToast } from 'react-native-toast-notifications';
import React, { createContext, useCallback, useContext, useMemo } from 'react';

import { Toast } from '@components/Toast';

import { withNotificationsProvider } from './NotificationsProvider';

type ToastProviderProps = {
  children: React.ReactNode;
};

export type ToastType = 'success' | 'error' | 'info' | 'loading' | 'warning';
type ToastOptionsInput = {};

type HideToastFn = (id: string) => void;
type ShowToastFn = (
  id: string,
  type: ToastType,
  title: React.ReactNode,
  body: React.ReactNode,
  options?: ToastOptionsInput,
) => void;

export type ToastContextValue = {
  hide: HideToastFn;
  show: ShowToastFn;
};

const ToastContext = createContext<ToastContextValue>({
  hide: () => 'missing-toast-context',
  show: () => 'missing-toast-context',
});

const MAX_DURATION = 24 * 60 * 60 * 1000;
const durationOfToastType: Record<ToastType, number> = {
  loading: MAX_DURATION, // persistent,
  info: 5000,
  success: 5000,
  error: MAX_DURATION,
  warning: MAX_DURATION,
};

const computeToastOptions = (id: string, type: ToastType) => {
  const duration = durationOfToastType[type];
  const isPersistent = type === 'loading';

  return {
    id,
    duration,
    swipeEnabled: isPersistent ? false : true, // allow swipe only if it's not persistent
  };
};

type ToastState = 'opened' | 'closed';
const toastState: Record<string, ToastState> = {};

export const ToastProvider = withNotificationsProvider<ToastProviderProps>(
  ({ children }) => {
    const toast = useNotificationsToast();

    const show: ShowToastFn = useCallback(
      (id, type, title, body) => {
        const toastNode = (
          <Toast
            type={type}
            title={title}
            body={body}
            isDismissible={type !== 'loading'}
            onClosePress={() => {
              toastState[id] = 'closed';
              toast.hide(id);
            }}
          />
        );

        const toastOptions = computeToastOptions(id, type);

        if (toastState[id] === 'opened') {
          toast.update(id, toastNode, toastOptions);
          return id;
        }

        toastState[id] = 'opened';
        return toast.show(toastNode, toastOptions);
      },
      [toast],
    );

    const value = useMemo(
      () => ({
        show,
        hide: (id: string) => {
          toastState[id] = 'closed';
          toast.hide(id);
        },
      }),
      [show, toast],
    );

    return (
      <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
    );
  },
);

export const useToast = () => useContext(ToastContext);
